import React from "react";
import PageHeader from "../../common/PageHeader";
import {
  PriceCheck,
  PersonAddAltOutlined,
  RecordVoiceOverOutlined,
  VideoLibraryOutlined,
  ShoppingBagOutlined,
  SaveOutlined,
  TokenOutlined,
  ArticleOutlined,
  MicOutlined,
  SubtitlesOutlined,
  SmartToyOutlined,
  LibraryMusicOutlined,
  AlbumOutlined,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Button,
  Avatar,
  alpha,
  useTheme,
  Divider,
  InputAdornment,
  Alert,
} from "@mui/material";
import { GlobalContext } from "../../context/GlobalContext";

const TASK_TYPES = (lang) => [
  {
    key: "inf_maker",
    icon: PersonAddAltOutlined,
    color: "#8B5CF6",
    title: lang?.tpCreateInfluencer || "Create Influencer",
    desc:
      lang?.tpCreateInfluencerDesc ||
      "Credits required to generate a new AI influencer",
  },
  {
    key: "inf_var_maker",
    icon: RecordVoiceOverOutlined,
    color: "#3B82F6",
    title: lang?.tpCreateVariable || "Create Influencer Variable",
    desc:
      lang?.tpCreateVariableDesc ||
      "Credits required to create a variable of an existing influencer",
  },
  {
    key: "content_video_maker",
    icon: VideoLibraryOutlined,
    color: "#059669",
    title: lang?.tpContentVideo || "Content Video Maker",
    desc:
      lang?.tpContentVideoDesc ||
      "Credits required to generate video from an existing template",
  },
  {
    key: "product_showcase_maker",
    icon: ShoppingBagOutlined,
    color: "#F59E0B",
    title: lang?.tpProductShowcase || "Product Showcase Video",
    desc:
      lang?.tpProductShowcaseDesc ||
      "Credits required to create a product showcase video",
  },
  {
    key: "text_generation",
    icon: ArticleOutlined,
    color: "#94A3B8",
    title: lang?.tpTextGen || "Text generation",
    desc:
      lang?.tpTextGenDesc ||
      "Credits per UGC script generation run (ten ad scripts from research)",
  },
  {
    key: "voice_generation",
    icon: MicOutlined,
    color: "#EC4899",
    title: lang?.tpVoiceGen || "Voice generation",
    desc:
      lang?.tpVoiceGenDesc ||
      "Credits per talking-head TTS synthesis (preview and avatar video voice)",
  },
  {
    key: "lyrics_generation",
    icon: SubtitlesOutlined,
    color: "#06B6D4",
    title: lang?.tpLyricsGen || "Lyrics generation",
    desc:
      lang?.tpLyricsGenDesc ||
      "Credits per AI lyrics job in Music Studio",
  },
  {
    key: "video_agent_generation",
    icon: SmartToyOutlined,
    color: "#A855F7",
    title: lang?.tpVideoAgent || "Video agent generation",
    desc:
      lang?.tpVideoAgentDesc ||
      "Credits per HeyGen video agent render job",
  },
  {
    key: "music_generation",
    icon: LibraryMusicOutlined,
    color: "#22C55E",
    title: lang?.tpMusicGen || "Music generation",
    desc:
      lang?.tpMusicGenDesc ||
      "Credits per full music track generation (standard)",
  },
  {
    key: "music_cover_generation",
    icon: AlbumOutlined,
    color: "#F97316",
    title: lang?.tpMusicCoverGen || "Music cover generation",
    desc:
      lang?.tpMusicCoverGenDesc ||
      "Credits per music generation when a persona / cover style is used",
  },
];

// ── Task Row ──────────────────────────────────────────────
const TaskRow = ({ item, value, onChange, theme }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "flex-start", sm: "center" }}
    spacing={2}
    px={2.5}
    py={2.5}
  >
    <Avatar
      sx={{
        width: 46,
        height: 46,
        bgcolor: alpha(item.color, 0.12),
        color: item.color,
        flexShrink: 0,
      }}
    >
      <item.icon sx={{ fontSize: 22 }} />
    </Avatar>

    <Box flex={1} minWidth={0}>
      <Typography variant="body2" fontWeight={700} color="text.primary">
        {item.title}
      </Typography>
      <Typography
        variant="caption"
        color="text.disabled"
        display="block"
        mt={0.3}
      >
        {item.desc}
      </Typography>
    </Box>

    <TextField
      type="number"
      value={value}
      onChange={(e) => onChange(item.key, e.target.value)}
      size="small"
      inputProps={{ min: 1, step: 1 }}
      sx={{
        width: 150,
        flexShrink: 0,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          fontWeight: 700,
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: item.color,
          },
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: alpha(item.color, 0.3),
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: alpha(item.color, 0.6),
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <TokenOutlined sx={{ fontSize: 16, color: item.color }} />
          </InputAdornment>
        ),
      }}
    />
  </Stack>
);

// ── Main Component ────────────────────────────────────────
const TaskPricing = ({ lang }) => {
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();

  const [credits, setCredits] = React.useState({
    inf_maker: "",
    inf_var_maker: "",
    content_video_maker: "",
    product_showcase_maker: "",
    text_generation: "",
    voice_generation: "",
    lyrics_generation: "",
    video_agent_generation: "",
    music_generation: "",
    music_cover_generation: "",
  });

  const [poyoPricingRaw, setPoyoPricingRaw] = React.useState("");
  const [poyoPricingMeta, setPoyoPricingMeta] = React.useState({ updatedAt: null, source: null });
  const [savingPoyo, setSavingPoyo] = React.useState(false);
  const [syncingPoyo, setSyncingPoyo] = React.useState(false);

  // ── Fetch existing values on mount
  async function getWebPvt() {
    const res = await hitAxios({
      path: "/api/web/get_web_pvt",
      post: false,
      admin: true,
    });
    if (res?.data?.success) {
      const d = res.data.data;
      const n = (v) => {
        const x = Number(v);
        return Number.isFinite(x) && x >= 1 ? x : "";
      };
      setCredits({
        inf_maker: n(d.inf_maker),
        inf_var_maker: n(d.inf_var_maker),
        content_video_maker: n(d.content_video_maker),
        product_showcase_maker: n(d.product_showcase_maker),
        text_generation: n(d.text_generation),
        voice_generation: n(d.voice_generation),
        lyrics_generation: n(d.lyrics_generation),
        video_agent_generation: n(d.video_agent_generation),
        music_generation: n(d.music_generation),
        music_cover_generation: n(d.music_cover_generation),
      });
    }
  }

  async function loadPoyoPricing() {
    const res = await hitAxios({
      path: "/api/admin/get_poyo_pricing",
      post: false,
      admin: true,
      showLoading: false,
      showSnackbar: false,
    });
    if (res?.data?.success && res.data.data) {
      const store = res.data.data;
      setPoyoPricingRaw(JSON.stringify(store, null, 2));
      setPoyoPricingMeta({ updatedAt: store.updatedAt || null, source: store.source || null });
    }
  }

  // ── Save updated credits
  async function handleSaveData() {
    await hitAxios({
      path: "/api/web/update_credit_set",
      post: true,
      admin: true,
      obj: {
        inf_maker: credits.inf_maker,
        inf_var_maker: credits.inf_var_maker,
        content_video_maker: credits.content_video_maker,
        product_showcase_maker: credits.product_showcase_maker,
        text_generation: credits.text_generation,
        voice_generation: credits.voice_generation,
        lyrics_generation: credits.lyrics_generation,
        video_agent_generation: credits.video_agent_generation,
        music_generation: credits.music_generation,
        music_cover_generation: credits.music_cover_generation,
      },
    });
  }

  const handleChange = (key, value) => {
    setCredits((prev) => ({
      ...prev,
      [key]: value === "" ? "" : Number(value),
    }));
  };

  React.useEffect(() => {
    getWebPvt();
    loadPoyoPricing();
  }, []);

  const syncPoyo = async () => {
    setSyncingPoyo(true);
    const res = await hitAxios({
      path: "/api/admin/sync_poyo_pricing",
      post: true,
      admin: true,
      obj: {},
      showLoading: false,
      showSnackbar: false,
      timeout: 120000,
    });
    setSyncingPoyo(false);
    if (res?.data?.success && res.data.data?.store) {
      const store = res.data.data.store;
      setPoyoPricingRaw(JSON.stringify(store, null, 2));
      setPoyoPricingMeta({ updatedAt: store.updatedAt || null, source: store.source || null });
    }
  };

  const savePoyo = async () => {
    let parsed;
    try {
      parsed = JSON.parse(poyoPricingRaw);
    } catch {
      return;
    }
    setSavingPoyo(true);
    const res = await hitAxios({
      path: "/api/admin/update_poyo_pricing",
      post: true,
      admin: true,
      obj: parsed,
      showLoading: false,
      showSnackbar: false,
    });
    setSavingPoyo(false);
    if (res?.data?.success && res.data.data) {
      const store = res.data.data;
      setPoyoPricingRaw(JSON.stringify(store, null, 2));
      setPoyoPricingMeta({ updatedAt: store.updatedAt || null, source: store.source || null });
    }
  };

  const tasks = TASK_TYPES(lang);

  return (
    <Box>
      <PageHeader
        icon={PriceCheck}
        title={lang?.taskPricing || "Task Pricing"}
        subtitle={lang?.tpSub || "Manage credit requirement for each task"}
        primaryAction={
          <Button
            size="large"
            variant="contained"
            startIcon={<SaveOutlined />}
            onClick={handleSaveData}
          >
            {lang?.saveChanges || "Save Changes"}
          </Button>
        }
      />

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        {lang?.tpActorsNote ||
          "HeyGen Actors use the same pricing as Create Influencer and Create Influencer Variable (character / influencer generation). There is no separate Actors row."}
      </Alert>

      <Card
        sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
      >
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {/* Task Rows */}
          {tasks.map((item, i) => (
            <React.Fragment key={item.key}>
              <TaskRow
                item={item}
                value={credits[item.key]}
                onChange={handleChange}
                theme={theme}
              />
              {i < tasks.length - 1 && (
                <Divider sx={{ mx: 2.5, opacity: 0.5 }} />
              )}
            </React.Fragment>
          ))}
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mt: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Box>
              <Typography variant="h6" fontWeight={800}>
                {lang?.adminProviderPricingTitle || "Provider Pricing + Retail Pricing"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {lang?.adminProviderPricingHint ||
                  "Sync provider pricing and adjust your retail multiplier / overrides. Used to show estimated $ cost in Video Studio."}
              </Typography>
              {(poyoPricingMeta.updatedAt || poyoPricingMeta.source) && (
                <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
                  {poyoPricingMeta.updatedAt ? `Updated: ${poyoPricingMeta.updatedAt}` : ""}
                  {poyoPricingMeta.source ? " · Source: managed provider" : ""}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={syncPoyo} disabled={syncingPoyo} sx={{ fontWeight: 800 }}>
                {syncingPoyo ? (lang?.syncing || "Syncing…") : lang?.sync || "Sync provider pricing"}
              </Button>
              <Button variant="contained" onClick={savePoyo} disabled={savingPoyo} sx={{ fontWeight: 800 }}>
                {savingPoyo ? (lang?.saving || "Saving…") : lang?.save || "Save"}
              </Button>
            </Stack>
          </Stack>

          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            {lang?.adminProviderPricingJsonHint ||
              "Edit the JSON if you need to override edge cases (e.g., a model not yet parsed)."}
          </Alert>

          <TextField
            fullWidth
            multiline
            minRows={12}
            value={poyoPricingRaw}
            onChange={(e) => setPoyoPricingRaw(e.target.value)}
            placeholder="{ ... }"
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default TaskPricing;
